// Contrato de la pantalla Cartera. `PortfolioRow` es la fila que la tabla sabe pintar; `api/mappers`
// traduce a ella el wire de `GET /backoffice/credits`, que ya trae el bloqueo del equipo resuelto.
import type { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import type { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type { LockEvidence } from '../lockPhase';

/**
 * Estado del equipo financiado en la columna «Bloqueo» del diseño. `unknown` es la lectura del MDM
 * que falló y `no-permission` es que el actor no puede verla: ninguno de los dos es «sin equipo».
 */
export type PortfolioLockState = 'locked' | 'unlocked' | 'no-device' | 'unknown' | 'no-permission';

/** Una fila de la cartera: crédito + lo agregado del plan de pagos + el estado de su equipo. */
export interface PortfolioRow {
    creditId: string;
    borrowerId: string;
    status: LoanCreditStatusEnum;
    /** Nivel congelado al originar. Ausente = el crédito no lo registró. */
    clientLevel?: ClientLevelEnum;
    /** Nombre del acreditado, o su id crudo cuando no se resolvió. Ausente = ni siquiera hay id. */
    customerName?: string;
    /** `false` cuando `customerName` es el id crudo: la celda pone `customerNote` en el `title`. */
    customerNameResolved: boolean;
    /** Por qué Cliente no tiene nombre. Es la TERCERA lectura: falla aparte de Producto y Tienda. */
    customerNote: string;
    /** Lo que PINTA la celda Producto: el modelo. Ausente = la venta no lo resolvió, y `productNote` dice por qué. */
    productLabel?: string;
    /** La marca cruda del contexto de venta. NO se pinta: solo alimenta el combo de filtro. */
    productBrand?: string;
    /** Por qué Producto quedó sin valor. Solo se pinta cuando `productLabel` está ausente. */
    productNote: string;
    /** Nombre de la tienda, o su id crudo cuando el padrón no lo tradujo. Ausente = ni siquiera hay id. */
    storeName?: string;
    /** `false` cuando `storeName` es el id crudo: la celda pone `storeNote` en el `title`. */
    storeNameResolved: boolean;
    /** Por qué Tienda no tiene nombre. Falla APARTE de Producto: son dos lecturas distintas. */
    storeNote: string;
    weeklyPaymentWithIvaCents: number;
    /** Cupones cobrados sobre el plazo: la celda «Cup» y su barra de avance. */
    paidInstallments: number;
    termWeeks: number;
    /** `0` es «ya lo pagó»; `null` es «no aplica», porque nunca contrajo la deuda. */
    outstandingBalanceCents: number | null;
    /** Capital, interés e IVA que faltan. Ausente = el silo todavía no la publica. */
    totalDebtCents?: number | null;
    /** `0` es al día; `null` es «sin cuotas fechadas», que NO es lo mismo. */
    arrearsDays: number | null;
    lockState: PortfolioLockState;
    /** Lo que el expediente leído sabe del candado; la lectura se deriva al pintar, con la hora de ese momento. */
    lockEvidence?: LockEvidence;
    imei?: string;
    /** Fecha de originación: la sub-línea de la columna CR y el filtro de fechas. */
    createdAt: string;
}

/** Valor de filtro que no filtra nada. */
export const ANY_FILTER = 'all';
export type AnyFilter = typeof ANY_FILTER;

/** Los tramos de mora del diseño. */
export type ArrearsBucket = 'none' | '1-7' | '8-30' | '31-60' | '61-90';

export interface PortfolioFilters {
    /** CR, cliente o IMEI. */
    search: string;
    /** Chips de Nivel: se combinan con OR. Vacío deja pasar todos. */
    levels: readonly ClientLevelEnum[];
    status: LoanCreditStatusEnum | AnyFilter;
    lock: PortfolioLockState | AnyFilter;
    /** Marca del equipo, no el modelo: el combo agrupa por marca, como el mock. */
    brand: string;
    store: string;
    arrears: ArrearsBucket | AnyFilter;
    /** Rangos en CENTAVOS; `null` es «sin tope». */
    balanceMinCents: number | null;
    balanceMaxCents: number | null;
    paymentMinCents: number | null;
    paymentMaxCents: number | null;
    /** Días de calendario `YYYY-MM-DD`; cadena vacía es «sin filtro». */
    fromDay: string;
    toDay: string;
}

/** Las 5 pastillas de arriba de la tabla: además de contar, filtran. */
export type PortfolioStatKey = 'all' | 'current' | 'arrears' | 'settled' | 'locked';

export const PORTFOLIO_STAT_KEYS: readonly PortfolioStatKey[] = [
    'all',
    'current',
    'arrears',
    'settled',
    'locked',
];

/** `null` = no hay fuente que contar. Nunca `0`, que afirmaría que no hay ninguno. */
export type PortfolioStats = Record<PortfolioStatKey, number | null>;

export type PortfolioSortKey =
    // 🆕 nuestro: el mock no ordena por fecha, pero con datos reales el folio entierra la cartera viva.
    | 'origination'
    | 'credit'
    | 'level'
    | 'customer'
    | 'product'
    | 'store'
    | 'payment'
    | 'progress'
    | 'balance'
    | 'status'
    | 'arrears'
    | 'lock';

export type SortDirection = 'asc' | 'desc';

export interface PortfolioSort {
    key: PortfolioSortKey;
    direction: SortDirection;
}

export const PORTFOLIO_PAGE_SIZE = 30;

export function emptyPortfolioFilters(): PortfolioFilters {
    return {
        search: '',
        levels: [],
        status: ANY_FILTER,
        lock: ANY_FILTER,
        brand: ANY_FILTER,
        store: ANY_FILTER,
        arrears: ANY_FILTER,
        balanceMinCents: null,
        balanceMaxCents: null,
        paymentMinCents: null,
        paymentMaxCents: null,
        fromDay: '',
        toDay: '',
    };
}
